import { useEffect, useRef, useState } from "react";
import { Send, Loader2, DraftingCompass, Plus, MessageSquareText, Paperclip, FileText, Image as ImageIcon, FileSearch, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { WSFile, WSMessage } from "@/hooks/useBridgetteWorkspace";
import { BridgetteMarkdown } from "./BridgetteMarkdown";

interface Props {
  messages: WSMessage[];
  files: WSFile[];
  selectedFile?: WSFile | null;
  selectedFileId?: string | null;
  isStreaming: boolean;
  onSend: (text: string) => void;
  onNewSession: () => void;
  onUpload: (file: File, category: string) => Promise<WSFile | null>;
  onSelectFile?: (file: WSFile) => void;
  onVoiceOpen?: () => void;
  disabled?: boolean;
  status?: string;
}

const CATEGORIES = [
  { value: "blueprint", label: "Blueprint" },
  { value: "adjuster_scope", label: "Adjuster Scope" },
  { value: "measurement_report", label: "Measurement Report" },
  { value: "insurance_claim", label: "Insurance Claim" },
  { value: "roof_photo", label: "Roof Photo" },
  { value: "damage_photo", label: "Damage Photo" },
  { value: "other", label: "Other" },
];

const getAnalysisLabel = (content: string) => {
  const lower = content.toLowerCase();
  if (lower.includes("supplement")) return "Supplement analysis";
  if (lower.includes("adjuster") || lower.includes("email")) return "Adjuster draft";
  if (lower.includes("scope") || lower.includes("estimate")) return "Scope review";
  return "Detailed response";
};

const getAnalysisPreview = (content: string) => {
  const paragraphs = content
    .split(/\n{2,}/)
    .map((part) => part.replace(/[#>*_`-]/g, " ").replace(/\s+/g, " ").trim())
    .filter((part) => part.length > 40);

  const preferred = paragraphs.find(
    (part) => !/claim|policy|insured|property address/i.test(part.slice(0, 40)) && part.length > 90,
  );

  const preview = preferred ?? paragraphs.sort((a, b) => b.length - a.length)[0] ?? content;
  return preview.length > 240 ? `${preview.slice(0, 237).trim()}…` : preview;
};

export function ConversationPane({
  messages,
  files,
  selectedFile,
  selectedFileId,
  isStreaming,
  onSend,
  onNewSession,
  onUpload,
  onSelectFile,
  onVoiceOpen,
  disabled = false,
  status,
}: Props) {
  const [input, setInput] = useState("");
  const [expandedMessages, setExpandedMessages] = useState<Record<number, boolean>>({});
  const [category, setCategory] = useState("blueprint");
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isStreaming]);

  const handleSend = () => {
    if (!input.trim() || isStreaming) return;
    onSend(input);
    setInput("");
  };

  const uploadSelectedFile = async (file: File | undefined) => {
    if (!file || disabled || uploading) return;
    setUploading(true);
    await onUpload(file, category);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await uploadSelectedFile(e.target.files?.[0]);
  };

  return (
    <section className="bridgette-workspace flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-card">
      <div className="flex items-center justify-between border-b border-border bg-background/80 px-5 py-3 backdrop-blur">
        <div className="flex min-w-0 items-center gap-2">
          <MessageSquareText className="h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold">Bridgette chat</h2>
            {selectedFile ? <p className="truncate text-xs text-muted-foreground">Active: {selectedFile.file_name}</p> : null}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onNewSession}>
          <Plus className="mr-1 h-4 w-4" /> New
        </Button>
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx"
        onChange={handleFile}
      />

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-5">
        <div className="mx-auto max-w-4xl space-y-4">
          {messages.length === 0 && (
            <div className="space-y-2 py-12 text-center animate-fade-in">
              <DraftingCompass className="mx-auto h-8 w-8 text-primary" />
              <h3 className="font-semibold">Hi, I'm Bridgette.</h3>
              <p className="mx-auto max-w-lg text-sm leading-6 text-muted-foreground">
                Put the document in here and chat with me in the same place. I read what is in the file, pull out line items and totals, compare measurements, flag supplement gaps, and draft the follow-up notes or emails you need next.
              </p>
              <div className="mx-auto mt-6 grid max-w-2xl gap-2 text-left sm:grid-cols-3">
                {[
                  "Reads scopes, claim docs, blueprints, reports, and photos",
                  "Builds evidence-backed line items, flags, and quantity checks",
                  "Drafts supplement notes, adjuster emails, and PDF-ready summaries",
                ].map((item) => (
                  <div key={item} className="rounded-lg border border-border bg-background px-3 py-3 text-xs leading-5 text-muted-foreground shadow-sm">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

          {messages.map((message, index) => {
            const showTextBubble = Boolean(message.content?.trim());
            const streamingIndicator = isStreaming && index === messages.length - 1 && !message.content;

            return (
              <div key={index} className={cn("flex animate-fade-in", message.role === "user" ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[92%] min-w-0", message.role === "assistant" ? "w-full" : "w-auto")}>
                  {message.role === "user" ? (
                    <div className="rounded-2xl bg-primary px-4 py-2.5 text-sm whitespace-pre-wrap text-primary-foreground">
                      {message.content}
                    </div>
                  ) : showTextBubble || streamingIndicator ? (
                    <div className="rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground shadow-sm animate-fade-in">
                      {message.content ? (
                        message.content.length > 900 ? (
                          expandedMessages[index] ? (
                            <div className="space-y-3">
                              <BridgetteMarkdown content={message.content} />
                              <Button type="button" variant="ghost" size="sm" onClick={() => setExpandedMessages((prev) => ({ ...prev, [index]: false }))}>
                                Collapse analysis
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="rounded-lg border border-border bg-muted/20 px-3 py-3 animate-fade-in">
                                <div className="text-[11px] font-semibold uppercase text-primary">{getAnalysisLabel(message.content)}</div>
                                <p className="mt-1 text-sm leading-relaxed text-foreground">{getAnalysisPreview(message.content)}</p>
                              </div>
                              <Button type="button" variant="outline" size="sm" onClick={() => setExpandedMessages((prev) => ({ ...prev, [index]: true }))}>
                                Expand full analysis
                              </Button>
                            </div>
                          )
                        ) : (
                          <BridgetteMarkdown content={message.content} />
                        )
                      ) : <Loader2 className="h-4 w-4 animate-spin" />}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-border bg-background/80 p-3 backdrop-blur">
        <div className="mx-auto max-w-4xl space-y-2">
          {files.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {files.map((file) => {
                const selected = file.id === selectedFileId;
                return (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => onSelectFile?.(file)}
                    className={cn(
                      "flex max-w-[220px] items-center gap-2 rounded-full border px-3 py-1 text-left text-xs transition-colors",
                      selected ? "border-primary/30 bg-primary/5 text-foreground" : "border-border bg-background text-muted-foreground hover:bg-muted/30",
                    )}
                  >
                    {file.file_type?.startsWith("image/") ? <ImageIcon className="h-3.5 w-3.5 shrink-0" /> : <FileText className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{file.file_name}</span>
                    {!file.vision_summary ? <FileSearch className="h-3.5 w-3.5 shrink-0 animate-pulse" /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}

          {status ? <p className="text-[11px] text-primary">{status}</p> : null}

          <div className="flex items-end gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-[60px] w-[168px] shrink-0 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-[60px] w-12 shrink-0"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
              aria-label="Attach document"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </Button>

            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-[60px] w-12 shrink-0 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
              onClick={onVoiceOpen}
              disabled={disabled}
              aria-label="Open Bridgette voice mode"
            >
              <Mic className="h-4 w-4" />
            </Button>

            <div className="flex min-w-0 flex-1 gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask Bridgette for evidence, supplement items, emails, or PDF drafts…"
            className="min-h-[60px] resize-none"
            disabled={isStreaming}
          />
              <Button onClick={handleSend} disabled={isStreaming || !input.trim()} size="icon" className="h-[60px] w-12 shrink-0">
                {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
